import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import { CAMPAIGN_STATE } from '@/lib/types';

interface CampaignStatusMessagesProps {
  firstPublishedAt: string | null | undefined;
  state: string | undefined;
  isLaunching?: boolean;
  /** In-flight pause/resume toggle transition, tracked to task completion. */
  transition?: 'pausing' | 'resuming' | null;
  /** Reports whether a spinner or confirmation is currently occupying the slot,
   * so the toolbar can hold back the next control (e.g. the Resume button)
   * until the sequence finishes (#5669). */
  onBusyChange?: (busy: boolean) => void;
}

/**
 * Single-slot status sequence for the campaign toolbar (#5669).
 *
 * Renders exactly ONE element at a time, in order:
 *   1. the in-flight transition spinner ("Pausing..." / "Resuming...")
 *   2. the confirmation for the completed transition ("Paused" / "Resumed" / "Launched")
 *   3. nothing
 *
 * State-change confirmations are detected from server data but HELD while a
 * transition is in flight — the backend commits the new state at task START,
 * so without the hold the confirmation fires beside the spinner (#5667).
 */
export function CampaignStatusMessages({
  firstPublishedAt,
  state,
  isLaunching,
  transition,
  onBusyChange,
}: CampaignStatusMessagesProps) {
  const [launchSuccess, setLaunchSuccess] = useState(false);
  const [resumeSuccess, setResumeSuccess] = useState(false);
  const [pauseSuccess, setPauseSuccess] = useState(false);

  const prevFirstPublishedAtRef = useRef(firstPublishedAt);
  const prevStateRef = useRef(state);
  const pendingLaunchRef = useRef(false);
  const pendingResumeRef = useRef(false);
  const pendingPauseRef = useRef(false);

  const inTransition = Boolean(isLaunching || transition);

  useEffect(() => {
    if (!prevFirstPublishedAtRef.current && firstPublishedAt) {
      if (inTransition) {
        pendingLaunchRef.current = true;
      } else {
        setLaunchSuccess(true);
      }
    }
    prevFirstPublishedAtRef.current = firstPublishedAt;
  }, [firstPublishedAt, inTransition]);

  useEffect(() => {
    if (prevStateRef.current === CAMPAIGN_STATE.PAUSED && state === CAMPAIGN_STATE.LIVE) {
      if (inTransition) {
        pendingResumeRef.current = true;
      } else {
        setResumeSuccess(true);
      }
    } else if (prevStateRef.current === CAMPAIGN_STATE.LIVE && state === CAMPAIGN_STATE.PAUSED) {
      if (inTransition) {
        pendingPauseRef.current = true;
      } else {
        setPauseSuccess(true);
      }
    }
    prevStateRef.current = state;
  }, [state, inTransition]);

  // Flush held confirmations the moment the transition finishes.
  useEffect(() => {
    if (inTransition) return;
    if (pendingLaunchRef.current) {
      pendingLaunchRef.current = false;
      setLaunchSuccess(true);
    }
    if (pendingResumeRef.current) {
      pendingResumeRef.current = false;
      setResumeSuccess(true);
    }
    if (pendingPauseRef.current) {
      pendingPauseRef.current = false;
      setPauseSuccess(true);
    }
  }, [inTransition]);

  const isBusy = Boolean(transition) || launchSuccess || resumeSuccess || pauseSuccess;
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  // One element at a time: spinner wins, then a single confirmation by priority.
  // Spinner and confirmation take the colour of the action (#5829): amber for
  // pausing, green for resuming/launching — matching the sidebar state icons.
  if (transition) {
    return (
      <span
        className={`h-9 pr-2 text-sm flex items-center gap-1 ${
          transition === 'pausing' ? 'text-amber-500' : 'text-green-600'
        }`}
      >
        <Spinner size="sm" />
        {transition === 'pausing' ? 'Pausing...' : 'Resuming...'}
      </span>
    );
  }

  const confirmation = launchSuccess
    ? { label: 'Launched', colour: 'text-[#047857]', clear: () => setLaunchSuccess(false) }
    : resumeSuccess
      ? { label: 'Resumed', colour: 'text-[#047857]', clear: () => setResumeSuccess(false) }
      : pauseSuccess
        ? { label: 'Paused', colour: 'text-amber-500', clear: () => setPauseSuccess(false) }
        : null;

  if (!confirmation) return null;

  return (
    <span
      onAnimationEnd={confirmation.clear}
      className={`h-9 pr-2 text-sm ${confirmation.colour} flex items-center gap-1 animate-status-message`}
    >
      <Check className="h-4 w-4" />
      {confirmation.label}
    </span>
  );
}
